import Button from '@/components/units/button/Button';
import Icon from '@/components/units/Icon';
import ProgressRing from '@/components/units/ProgressRing/ProgressRing';
import { getFundLimit } from '@/services/rest/fund-limit';
import Sentry from '@/utility/errorTracking';
import { formatAmountWithoutSymbol, splitFormatAmount } from '@/utility/formatter';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const NetAvailableMargin = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [userFundBalance, setUserFundBalance] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const popoverRef = useRef(null);

  const isAddButtonVisible = useSelector(
    (state) => state?.featureflag?.netAvailableMargin?.addBtn?.isVisible
  );
  const isWithdrawButtonVisible = useSelector(
    (state) => state?.featureflag?.netAvailableMargin?.withdrawBtn?.isVisible
  );

  useEffect(() => {
    const fetchNetAvailableMargin = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, infoMsg } = await getFundLimit();
        if (
          data?.availableBalance === null ||
          data?.amountUtilized === null ||
          data?.totalBalance === null
        ) {
          Sentry.captureException(
            new Error('Fund limits data is incomplete: availableBalance or amountUtilized is null'),
            {
              infoMsg,
              data,
            }
          );
        }
        setUserFundBalance(data);
      } catch (error) {
        console.error('Error fetching user balance data:', error);
        setError(error?.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNetAvailableMargin();
  }, []);

  useEffect(() => {
    const hidePopover = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowDetails(false);
      }
    };

    window.addEventListener('click', hidePopover);

    return () => {
      window.removeEventListener('click', hidePopover);
    };
  }, [showDetails]);

  const togglePopover = (e) => {
    e.stopPropagation();
    setShowDetails((prevState) => !prevState);
  };

  const netAvailableMargin = userFundBalance.availableBalance ?? 0.0;

  const formattedNetAvailableMargin = splitFormatAmount(netAvailableMargin);
  const formattedMargin = formatAmountWithoutSymbol(userFundBalance.totalBalance).split('.');
  const formattedUtilization = formatAmountWithoutSymbol(userFundBalance.amountUtilized).split('.');
  const fullNetAvailableMargin = formatAmountWithoutSymbol(userFundBalance.availableBalance).split(
    '.'
  );

  const hasError =
    userFundBalance.amountUtilized == null ||
    userFundBalance.availableBalance == null ||
    userFundBalance.totalBalance == null;

  return (
    <div className="tw-relative">
      <div className="tw-flex tw-items-center tw-gap-2" onClick={togglePopover}>
        <button className="tw-text-[#F8FBFB] tw-font-dmsansreg tw-text-base">
          Net Available Margin
        </button>

        <Icon iconName="info-green" wrapperStyle="tw-cursor-pointer" />

        {isLoading ? (
          <div className="tw-w-4">
            <ProgressRing
              size="2px"
              color="#75F7FF"
              backgroundColor="#051F23"
              animated
              progress={0.7}
              offset={0}
            />
          </div>
        ) : (
          <button className="tw-text-xl tw-text-white tw-font-dmsanssemibold">
            <span className="tw-font-ibmplexsansmed tw-mr-1">
              {formattedNetAvailableMargin.symbol}
            </span>
            {error || hasError ? (
              '-'
            ) : (
              <>
                <span>{formattedNetAvailableMargin.integer}.</span>
                <span className="tw-text-sm tw-font-dmsansbold">
                  {formattedNetAvailableMargin.decimal || '00'}
                </span>
                {formattedNetAvailableMargin.notation}
              </>
            )}
          </button>
        )}

        <Icon
          iconName="down-arrow"
          wrapperStyle={`tw-text-primaryBlue tw-w-6 tw-h-6 tw-transition-transform tw-duration-200 tw-cursor-pointer ${showDetails ? ' tw-rotate-180' : ''}`}
        />
      </div>

      {showDetails && (
        <div
          className="tw-absolute tw-top-10 tw-right-0 tw-min-w-full tw-max-w-[30rem] tw-bg-[#051F23] tw-rounded-bl-2xl tw-rounded-br-2xl tw-px-4 tw-pb-4 tw-pt-6 tw-text-[#E6EEF0]"
          ref={popoverRef}
        >
          {error || hasError ? (
            <div className="tw-flex tw-justify-center tw-items-center">No Data Found!</div>
          ) : (
            <>
              <div className="tw-flex tw-flex-col tw-justify-between tw-gap-4 tw-rounded-xl tw-bg-[#408C8C33] tw-p-4 tw-mb-2 tw-font-dmsansmed tw-text-sm">
                <div className="tw-flex tw-justify-between tw-gap-2">
                  <span className="tw-text-center tw-w-[5.5rem]">Margin</span>
                  <div className="tw-font-dmsanssemibold">
                    <span className="tw-text-base">{formattedMargin[0]}.</span>
                    <span>{formattedMargin[1] || '00'}</span>
                  </div>
                </div>

                <div className="tw-flex tw-justify-between tw-gap-2">
                  <span className="tw-text-right tw-w-[5.5rem]">- Utilization</span>
                  <div className="tw-font-dmsanssemibold">
                    <span className="tw-text-base">{formattedUtilization[0]}.</span>
                    <span>{formattedUtilization[1] || '00'}</span>
                  </div>
                </div>
              </div>

              <div className="tw-rounded-xl tw-bg-[#408C8C33] tw-px-4 tw-py-2 tw-mb-4 tw-text-sm tw-flex tw-justify-between tw-items-center tw-gap-2 tw-flex-wrap">
                <span className="tw-font-dmsansmed">Net Available Margin</span>
                <div className="tw-font-dmsanssemibold tw-text-right tw-flex-grow">
                  <span className="tw-text-base">{fullNetAvailableMargin[0]}.</span>
                  <span>{fullNetAvailableMargin[1] || '00'}</span>
                </div>
              </div>

              <div className="tw-flex tw-gap-2 tw-justify-center">
                {isAddButtonVisible && (
                  <Button
                    className="tw-font-dmsanssemibold tw-py-2 tw-max-w-36"
                    variant="aqua"
                    rounded
                    onClick={() => {}}
                  >
                    <Icon iconName="right-arrow" /> Add
                  </Button>
                )}

                {isWithdrawButtonVisible && (
                  <Button
                    className="tw-font-dmsanssemibold tw-py-2 tw-max-w-36"
                    variant="danger"
                    rounded
                    onClick={() => {}}
                  >
                    <Icon iconName="right-arrow" /> Withdraw
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NetAvailableMargin;
